import { ssoApi } from "@sso/apis";
import { ApiMethodKeys, EntityName, notificationsMsgs, SnackbarKeys } from "@sso/constant";
import { closeDialog } from "@sso/store/slices/dialog";
import { clearOrganisationMergeArray } from "@sso/store/slices/organisation";
import { pushSnackbar } from "@sso/store/slices/snackbar";
import { ApiPageResponse, MergeOrgsRequest, Organisation } from "@sso/types";

import { CacheKeys } from "../cacheKeys";
import { Endpoints } from "../endpoints";

export const organisationsApi = ssoApi.enhanceEndpoints({ addTagTypes: [CacheKeys.ORGS] }).injectEndpoints({
  endpoints: builder => ({
    organisations: builder.query<ApiPageResponse<Organisation>, string>({
      query: query => ({ url: `${Endpoints.ORGS}?${query}` }),
      providesTags: [CacheKeys.ORGS],
    }),
    addOrganisation: builder.mutation<Organisation, Organisation>({
      query: data => ({ url: `${Endpoints.ORGS}/create`, method: ApiMethodKeys.POST, data }),
      invalidatesTags: [CacheKeys.ORGS],
      onQueryStarted(_, { dispatch, queryFulfilled }) {
        queryFulfilled.then(() => {
          dispatch(pushSnackbar({ type: SnackbarKeys.SUCCESS, title: notificationsMsgs.ADD_ITEM(EntityName.ORG) }));
          dispatch(closeDialog());
        });
      },
    }),
    editOrganisation: builder.mutation<Organisation, Organisation>({
      query: data => ({ url: `${Endpoints.ORGS}/update`, method: ApiMethodKeys.PUT, data }),
      invalidatesTags: [CacheKeys.ORGS],
      onQueryStarted(_, { dispatch, queryFulfilled }) {
        queryFulfilled.then(() => {
          dispatch(pushSnackbar({ type: SnackbarKeys.SUCCESS, title: notificationsMsgs.Edit_ITEM(EntityName.ORG) }));
          dispatch(closeDialog());
        });
      },
    }),
    deleteOrganisation: builder.mutation<void, number>({
      query: organisationId => ({ url: Endpoints.ORGS, params: { organisationId }, method: ApiMethodKeys.DELETE }),
      invalidatesTags: [CacheKeys.ORGS],
      onQueryStarted(_, { dispatch, queryFulfilled }) {
        queryFulfilled.then(() => {
          dispatch(pushSnackbar({ type: SnackbarKeys.SUCCESS, title: notificationsMsgs.DELETE_ITEM(EntityName.ORG) }));
          dispatch(closeDialog());
        });
      },
    }),
    mergeOrganisations: builder.mutation<void, MergeOrgsRequest>({
      query: data => ({ url: `${Endpoints.ORGS}/merge`, method: ApiMethodKeys.POST, data }),
      invalidatesTags: [CacheKeys.ORGS],
      onQueryStarted(_, { dispatch, queryFulfilled }) {
        queryFulfilled.then(() => {
          dispatch(pushSnackbar({ type: SnackbarKeys.SUCCESS, title: notificationsMsgs.MERGE_ORGANISATIONS }));
          dispatch(clearOrganisationMergeArray());
          dispatch(closeDialog());
        });
      },
    }),
    searchOrganisations: builder.mutation<Organisation[], string>({
      query: query => ({ url: `${Endpoints.ORGS}/search/${query}` }),
    }),
  }),
});

export const {
  useOrganisationsQuery,
  useAddOrganisationMutation,
  useEditOrganisationMutation,
  useDeleteOrganisationMutation,
  useMergeOrganisationsMutation,
  useSearchOrganisationsMutation
} = organisationsApi;
